import { Text, VStack } from '@chakra-ui/react'
import { NodeProps, Position } from '@xyflow/react'
import React, { useMemo } from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import { QueueNodeData } from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { QueueSelector } from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useQueueNode } from './hooks/useQueueNode'

export interface QueueNodeProps extends NodeProps {
  extensionMap?: Map<number, ExtensionInstance>
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void
  showDeleteButton?: boolean
  // Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

const QueueNodeComponent: React.FC<QueueNodeProps> = ({
  id,
  data,
  extensionMap: _extensionMap, // Reserved for future use (e.g., display formatting)
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const nodeData = data as QueueNodeData & Record<string, unknown>
  const { currentQueueId, handleQueueChange, hasData } = useQueueNode(
    id,
    nodeData,
  )
  const t = useSwitchboardT()

  // Add two custom handles on the right side: On Timeout (top), On Escape (bottom)
  // Note: React Flow Position enum doesn't support TopRight/BottomRight, so we use Position.Right
  // with custom styles. The converter uses handle IDs (onTimeout, onEscape) to map to legacy anchors
  // (TopRight, BottomRight) - see FIXED_HANDLE_ANCHORS.queue
  // Memoize handles array to prevent re-creating on every render
  const queueHandles = useMemo(
    () => [
      {
        id: 'onTimeout',
        position: Position.Right as Position, // Mapped to TopRight in legacy via handle ID
        style: {
          top: '0px',
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.transfer.onTimeout', 'On Timeout'),
      },
      {
        id: 'onEscape',
        position: Position.Right as Position, // Mapped to BottomRight in legacy via handle ID
        style: {
          top: 'calc(100%)',
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.queue.onEscape', 'On Escape'),
      },
    ],
    [t],
  )

  return (
    <BaseNode
      label={text(t, 'switchboard.nodeLabels.queue', 'Queue')}
      showPlaceholder={!hasData}
      sourceHandlePosition={null}
      additionalSourceHandles={queueHandles}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
    >
      <VStack align="stretch" gap={2}>
        <QueueSelector value={currentQueueId} onChange={handleQueueChange} />
        {nodeData.maxWaitTime && (
          <VStack align="stretch" gap={1}>
            <Text fontSize="xs" color="fg.muted">
              {text(t, 'switchboard.nodes.queue.maxWait', 'Max Wait:')}{' '}
              {nodeData.maxWaitTime}s
            </Text>
          </VStack>
        )}
      </VStack>
    </BaseNode>
  )
}

export const QueueNode = React.memo(QueueNodeComponent, nodePropsAreEqual)
